import { useApi } from '@mainstage/core-plugin-api';
import { panelCatalogApiRef } from '../../../api';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { Target } from '@agilelab/plugin-wb-builder-backend';
import {
  Action,
  DeploymentUnitStatus,
  generateURNByKind,
  ProvisioningPreviewResponseOperation,
  Reason,
  TargetStatus,
} from '@agilelab/plugin-wb-builder-common';
import {
  DeploymentPreview,
  DeploymentPreviewData,
  ComponentWithStatus,
} from '../types';
import yaml from 'yaml';
import { useCallback } from 'react';

const getMatchedComponents = (
  components: Record<string, any>[],
  operations: ProvisioningPreviewResponseOperation[],
): DeploymentPreview[] =>
  components
    .filter(c => operations.find(op => op.componentId === c.id))
    .map(c => {
      const operation = operations.find(op => op.componentId === c.id);

      return {
        ...c,
        newState: operation?.targetStatus,
        action: operation?.action,
      } as DeploymentPreview;
    });

const getNotInTargetDescriptorOperations = (
  operations: ProvisioningPreviewResponseOperation[],
) =>
  operations.filter(
    op =>
      op.reason === Reason.NOT_IN_TARGET_DESCRIPTOR ||
      op.additionalReasons?.includes(Reason.NOT_IN_TARGET_DESCRIPTOR),
  );

const getCleanUpOperations = (
  operations: ProvisioningPreviewResponseOperation[],
) =>
  operations.filter(
    op =>
      op.reason === Reason.CLEANUP ||
      op.additionalReasons?.includes(Reason.CLEANUP),
  );

export function useDeploymentPreviews() {
  const {
    releaseDetailAction,
    components,
    release,
    currentDescriptor,
    entity,
    queryParamEnvironment,
    environment,
    deploymentUnitStatusState,
    projectName,
  } = useReleaseDetailPageContext();

  const panelCatalogApi = useApi(panelCatalogApiRef);

  const enabled = !!(releaseDetailAction && deploymentUnitStatusState.value);

  const fetchData = useCallback(async (): Promise<
    DeploymentPreviewData | undefined
  > => {
    if (!releaseDetailAction || !deploymentUnitStatusState.value)
      return undefined;

    const selCount = components.filter(c => c.selected).length;
    const ignoreBySelection = (c: ComponentWithStatus) =>
      !(c.selected || selCount === 0);

    const rootComponentId = generateURNByKind(
      entity.metadata.name,
      entity.kind ?? 'dp',
    );

    const getAction = (c: ComponentWithStatus) => {
      if (ignoreBySelection(c)) return Action.NO_ACTION;
      return releaseDetailAction === Action.DEPLOY
        ? Action.DEPLOY
        : Action.UNDEPLOY;
    };

    const getTarget = (c: ComponentWithStatus) => {
      if (ignoreBySelection(c))
        return c.status === 'NOT_DEPLOYED'
          ? TargetStatus.NOT_DEPLOYED
          : TargetStatus.DEPLOYED;
      return releaseDetailAction === Action.DEPLOY
        ? TargetStatus.DEPLOYED
        : TargetStatus.NOT_DEPLOYED;
    };

    const targets = components.flat().map(getTarget);
    let entityTarget: Target;

    if (
      releaseDetailAction === Action.UNDEPLOY &&
      targets.length > 0 &&
      targets.includes(TargetStatus.DEPLOYED)
    ) {
      entityTarget = {
        action: Action.DEPLOY,
        targetStatus: TargetStatus.DEPLOYED,
        componentId: rootComponentId,
      };
    } else {
      entityTarget = {
        action: releaseDetailAction,
        targetStatus:
          releaseDetailAction === Action.DEPLOY
            ? TargetStatus.DEPLOYED
            : TargetStatus.NOT_DEPLOYED,
        componentId: rootComponentId,
      };
    }

    const componentTargets: Target[] = components.map(c => ({
      action: getAction(c),
      targetStatus: getTarget(c),
      componentId: c.id,
    }));

    const subcomponentsTargets: Target[] = components.flatMap(c =>
      (c.components ?? []).map(({ id: componentId }) => ({
        action: getAction(c),
        targetStatus: getTarget(c),
        componentId,
      })),
    );

    const deploymentUnits =
      await panelCatalogApi.getDeploymentUnitsProvisioningPreview(
        generateURNByKind(
          release.metadata.projectName ?? release.metadata.dataProductName,
          release.metadata.projectKind ?? 'system',
        ),
        queryParamEnvironment ?? environment.name,
        {
          descriptor: yaml.parse(currentDescriptor),
          targetStatus: [
            entityTarget,
            ...componentTargets,
            ...subcomponentsTargets,
          ],
          removeData: false,
          preventRedeploy: false,
        },
      );

    const rootComponentOperation = deploymentUnits.operations.find(
      op => op.componentId === rootComponentId,
    );
    const rootComponentFromStatus =
      deploymentUnitStatusState.value.provisioningDetails?.componentsStatus?.find(
        c =>
          c?.componentId ===
          generateURNByKind(
            release.metadata.projectName ?? release.metadata.dataProductName,
            release.metadata.projectKind ?? 'dp',
          ),
      );

    const rootComponent: DeploymentPreview = {
      kind: entity?.spec?.type ?? entity.kind,
      name: projectName,
      status:
        (rootComponentFromStatus?.status as DeploymentUnitStatus) ??
        DeploymentUnitStatus.NOT_DEPLOYED,
      statusVersion:
        rootComponentFromStatus?.descriptorVersion ?? release.metadata.version,
      action: rootComponentOperation?.action,
      newState: rootComponentOperation?.targetStatus,
      id: rootComponentId,
      description: entity.metadata.description ?? '',
      fullyQualifiedName: null,
      version: release.metadata.version,
      infrastructureTemplateId: '',
      useCaseTemplateId: '',
      dependsOn: rootComponentOperation?.dependantOn ?? [],
      platform: '',
      technology: '',
      outputPortType: '',
      creationDate: new Date(release.metadata.createdAt),
      startDate: new Date(release.metadata.createdAt),
      processDescription: null,
      tags: entity.metadata.tags ?? [],
    };

    const subcomponents: Record<string, any>[] = components.flatMap(c =>
      (c.components ?? []).map(subcomponent => ({
        ...subcomponent,
        status:
          (deploymentUnitStatusState.value?.provisioningDetails?.componentsStatus.find(
            child => child.componentId === `${c.id}:${subcomponent.name}`,
          )?.status as DeploymentUnitStatus) ?? c.status,
        statusVersion:
          deploymentUnitStatusState.value?.provisioningDetails?.componentsStatus.find(
            child => child.componentId === `${c.id}:${subcomponent.name}`,
          )?.descriptorVersion ?? c.statusVersion,
        parent: c.id,
      })),
    );

    const fullComponents = components.map(c => ({
      ...c,
      components: getMatchedComponents(
        subcomponents.filter(subcomponent => subcomponent.parent === c.id),
        deploymentUnits.operations,
      ),
      dependsOn: [...(c.dependsOn ?? []), rootComponentId],
    }));

    return {
      previews: getMatchedComponents(
        fullComponents,
        deploymentUnits.operations,
      ),
      subcomponentsPreviews: getMatchedComponents(
        subcomponents,
        deploymentUnits.operations,
      ),
      notInTargetDescriptorOperations: getNotInTargetDescriptorOperations(
        deploymentUnits.operations,
      ).map(op => op.componentId),
      cleanUpOperations: getCleanUpOperations(deploymentUnits.operations).map(
        op => op.componentId,
      ),
      rootComponent: rootComponentOperation ? rootComponent : undefined,
      outcome: deploymentUnits.outcome,
    };
  }, [
    releaseDetailAction,
    deploymentUnitStatusState.value,
    components,
    release,
    currentDescriptor,
    entity,
    queryParamEnvironment,
    environment,
    projectName,
    panelCatalogApi,
  ]);

  return { fetchData, enabled };
}
